import type {
  SignupRequest, ChallengeResponse, LoginRequest,
  RecoveryChallengeResponse, RecoveryCompleteRequest, PasswordChangeRequest,
  MeResponse, Activity, CreateActivityRequest, UpdateActivityRequest,
  TagSuggestion, ProfileUpdateRequest,
  PublicListResponse, FeedbackSubmitRequest, FeedbackEntry, FeedbackUpdateRequest,
  AdminUser, AdminRoleUpdate,
  PublicSettings, SettingsUpdateRequest, InviteEntry,
  FriendEntry, BlockEntry, AddByUsernameRequest,
} from '../../../shared/types';

const BASE = '/api';

async function http<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = await res.json(); } catch { /* ignore */ }
    const err = new ApiError(res.status, detail);
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly detail: unknown) {
    super(`API ${status}: ${JSON.stringify(detail)}`);
    this.name = 'ApiError';
  }
}

// --- auth -------------------------------------------------------------------
export const api = {
  signup: (body: SignupRequest) =>
    http<MeResponse>('/auth/signup', { method: 'POST', body: JSON.stringify(body) }),
  challenge: (email: string) =>
    http<ChallengeResponse>('/auth/challenge', { method: 'POST', body: JSON.stringify({ email }) }),
  login: (body: LoginRequest) =>
    http<MeResponse>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => http<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => http<MeResponse>('/auth/me'),
  passwordChange: (body: PasswordChangeRequest) =>
    http<{ ok: true }>('/auth/password', { method: 'POST', body: JSON.stringify(body) }),
  recoveryChallenge: (email: string) =>
    http<RecoveryChallengeResponse>('/auth/recovery-challenge', {
      method: 'POST', body: JSON.stringify({ email }),
    }),
  recoveryComplete: (body: RecoveryCompleteRequest) =>
    http<{ ok: true }>('/auth/recovery-complete', {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateProfile: (body: ProfileUpdateRequest) =>
    http<MeResponse>('/auth/profile', {
      method: 'PATCH', body: JSON.stringify(body),
    }),

  // --- activities -----------------------------------------------------------
  listActivities: (opts?: { archived?: '1' | 'only'; hidden?: '1' | 'only' }) => {
    const qs = new URLSearchParams();
    if (opts?.archived) qs.set('archived', opts.archived);
    if (opts?.hidden) qs.set('hidden', opts.hidden);
    const q = qs.toString();
    return http<Activity[]>(`/activities${q ? `?${q}` : ''}`);
  },
  getActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}`),
  createActivity: (body: CreateActivityRequest) =>
    http<Activity>('/activities', { method: 'POST', body: JSON.stringify(body) }),
  updateActivity: (id: string, body: UpdateActivityRequest) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  deleteActivity: (id: string) =>
    http<{ ok: true }>(`/activities/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  heartActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}/heart`, { method: 'POST' }),
  unheartActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}/heart`, { method: 'DELETE' }),
  doneActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}/done`, { method: 'POST' }),
  undoneActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}/done`, { method: 'DELETE' }),
  archiveActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}/archive`, { method: 'POST' }),
  unarchiveActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}/archive`, { method: 'DELETE' }),
  hideActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}/hide`, { method: 'POST' }),
  unhideActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}/hide`, { method: 'DELETE' }),
  bookmarkActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}/bookmark`, { method: 'POST' }),
  unbookmarkActivity: (id: string) =>
    http<Activity>(`/activities/${encodeURIComponent(id)}/bookmark`, { method: 'DELETE' }),
  sortActivity: (id: string, position: number) =>
    http<{ ok: true }>(`/activities/${encodeURIComponent(id)}/sort`, {
      method: 'PATCH', body: JSON.stringify({ position }),
    }),

  // --- tags -----------------------------------------------------------------
  tagSuggestions: (q: string, limit = 20) =>
    http<TagSuggestion[]>(`/tags?q=${encodeURIComponent(q)}&limit=${limit}`),
  // Moderator/admin: delete a public tag entirely (also detaches it from all
  // activities). Forbidden for regular users.
  deleteTag: (name: string) =>
    http<void>(`/tags/${encodeURIComponent(name)}`, { method: 'DELETE' }),

  // --- users (opt-in public list) -------------------------------------------
  publicList: (username: string) =>
    http<PublicListResponse>(`/users/${encodeURIComponent(username)}/list`),

  // --- feedback -------------------------------------------------------------
  submitFeedback: (body: FeedbackSubmitRequest) =>
    http<FeedbackEntry>('/feedback', { method: 'POST', body: JSON.stringify(body) }),
  listFeedback: () => http<FeedbackEntry[]>('/feedback'),
  updateFeedback: (id: string, body: FeedbackUpdateRequest) =>
    http<FeedbackEntry>(`/feedback/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  deleteFeedback: (id: string) =>
    http<{ ok: true }>(`/feedback/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // --- admin (admin role only) ----------------------------------------------
  adminListUsers: () => http<AdminUser[]>('/admin/users'),
  adminSetRole: (id: string, body: AdminRoleUpdate) =>
    http<AdminUser>(`/admin/users/${encodeURIComponent(id)}/role`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),

  // --- settings -------------------------------------------------------------
  getSettings: () => http<PublicSettings>('/settings'),
  updateSettings: (body: SettingsUpdateRequest) =>
    http<PublicSettings>('/settings', { method: 'PATCH', body: JSON.stringify(body) }),

  // --- invites --------------------------------------------------------------
  listInvites: () => http<InviteEntry[]>('/invites'),
  createInvite: () => http<InviteEntry>('/invites', { method: 'POST' }),
  cancelInvite: (token: string) =>
    http<{ ok: true }>(`/invites/${encodeURIComponent(token)}`, { method: 'DELETE' }),

  // --- friends & blocks -----------------------------------------------------
  listFriends: () => http<FriendEntry[]>('/friends'),
  listIncomingFriends: () => http<FriendEntry[]>('/friends/incoming'),
  addFriend: (body: AddByUsernameRequest) =>
    http<FriendEntry>('/friends', { method: 'POST', body: JSON.stringify(body) }),
  removeFriend: (userId: string) =>
    http<{ ok: true }>(`/friends/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
  listBlocks: () => http<BlockEntry[]>('/friends/blocks'),
  blockUser: (userId: string) =>
    http<BlockEntry>('/friends/blocks', { method: 'POST', body: JSON.stringify({ user_id: userId }) }),
  unblockUser: (userId: string) =>
    http<{ ok: true }>(`/friends/blocks/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
};
